import React from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Slider,
  Switch,
  FormControlLabel,
  LinearProgress,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import BuildIcon from '@mui/icons-material/Build';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { RobotJoint } from '../types';

interface JointTestOverlayProps {
  joints: RobotJoint[];
  currentIndex: number;
  results: Map<string, 'pass' | 'fail' | 'fixed'>;
  fixes: Map<
    string,
    {
      limits?: { lower: number; upper: number };
      origLimits: { lower: number; upper: number };
      axisFlipped: boolean;
      continuous?: boolean;
    }
  >;
  onPass: () => void;
  onStartFix: () => void;
  onExit: () => void;
  fixing: boolean;
  fixLimits: { lower: number; upper: number };
  onFixLimitsChange: (lower: number, upper: number) => void;
  axisFlipped: boolean;
  onFlipAxis: () => void;
  fixContinuous: boolean;
  onToggleContinuous: () => void;
  onApplyFix: () => void;
  onSkipFix: () => void;
}

const JointTestOverlay: React.FC<JointTestOverlayProps> = ({
  joints,
  currentIndex,
  results,
  onPass,
  onStartFix,
  onExit,
  fixing,
  fixLimits,
  onFixLimitsChange,
  axisFlipped,
  onFlipAxis,
  fixContinuous,
  onToggleContinuous,
  onApplyFix,
  onSkipFix,
}) => {
  const isComplete = currentIndex >= joints.length;
  const current = isComplete ? null : joints[currentIndex];
  const progress = joints.length > 0 ? (currentIndex / joints.length) * 100 : 0;

  return (
    <>
      {/* Top progress bar */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 15,
          backgroundColor: 'rgba(10, 10, 10, 0.9)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          px: 3,
          py: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Joint Test — {isComplete ? 'Complete' : `${currentIndex + 1} / ${joints.length}`}
          </Typography>
          <IconButton onClick={onExit} size="small" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 3,
            backgroundColor: 'rgba(255,255,255,0.06)',
            '& .MuiLinearProgress-bar': { backgroundColor: '#EF8997' },
          }}
        />
      </Box>

      {/* Complete state */}
      {isComplete && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 15,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            All joints tested
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 3 }}>
            {Array.from(results.entries()).map(([name, result]) => (
              <Chip
                key={name}
                label={name.length > 12 ? name.slice(0, 12) + '...' : name}
                size="small"
                color={result === 'pass' ? 'success' : result === 'fixed' ? 'warning' : 'error'}
                variant="outlined"
                sx={{ fontSize: 10 }}
              />
            ))}
          </Box>
          <Button
            variant="contained"
            onClick={onExit}
            sx={{
              backgroundColor: '#EF8997',
              '&:hover': { backgroundColor: '#e57383' },
            }}
          >
            Done
          </Button>
        </Box>
      )}

      {/* Bottom action panel */}
      {current && !fixing && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 15,
            backgroundColor: 'rgba(10, 10, 10, 0.9)',
            backdropFilter: 'blur(8px)',
            borderTop: '1px solid rgba(255, 255, 255, 0.06)',
            p: 3,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {current.name}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mb: 2, display: 'block' }}>
            {current.type} — Does this joint move correctly?
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<CheckIcon />}
              onClick={onPass}
              sx={{
                flex: 1,
                backgroundColor: '#51cf66',
                color: '#000',
                '&:hover': { backgroundColor: '#40c057' },
              }}
            >
              Pass
            </Button>
            <Button
              variant="outlined"
              startIcon={<BuildIcon />}
              onClick={onStartFix}
              sx={{ flex: 1, borderColor: '#EF8997', color: '#EF8997' }}
            >
              Fix
            </Button>
          </Box>
        </Box>
      )}

      {/* Fix panel */}
      {current && fixing && (
        <Box
          sx={{
            position: 'absolute',
            top: 60,
            right: 16,
            bottom: 16,
            width: 320,
            zIndex: 15,
            backgroundColor: 'rgba(10, 10, 10, 0.9)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 2,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            Fix: {current.name}
          </Typography>

          {!fixContinuous && (
            <>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1 }}>
                Lower limit (rad)
              </Typography>
              <Slider
                value={fixLimits.lower}
                min={-Math.PI * 2}
                max={0}
                step={0.01}
                onChange={(_, v) => onFixLimitsChange(v as number, fixLimits.upper)}
                size="small"
                sx={{ color: '#EF8997', mb: 2 }}
              />
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1 }}>
                Upper limit (rad)
              </Typography>
              <Slider
                value={fixLimits.upper}
                min={0}
                max={Math.PI * 2}
                step={0.01}
                onChange={(_, v) => onFixLimitsChange(fixLimits.lower, v as number)}
                size="small"
                sx={{ color: '#EF8997', mb: 2 }}
              />
            </>
          )}

          <Button
            variant="outlined"
            size="small"
            startIcon={<SwapHorizIcon />}
            onClick={onFlipAxis}
            sx={{
              mb: 1.5,
              borderColor: axisFlipped ? '#EF8997' : 'rgba(255,255,255,0.2)',
              color: axisFlipped ? '#EF8997' : 'rgba(255,255,255,0.7)',
            }}
          >
            {axisFlipped ? 'Axis Flipped' : 'Flip Axis'}
          </Button>

          <FormControlLabel
            control={
              <Switch
                checked={fixContinuous}
                onChange={onToggleContinuous}
                size="small"
                sx={{ '& .Mui-checked': { color: '#EF8997' } }}
              />
            }
            label={<Typography variant="caption">Continuous rotation</Typography>}
            sx={{ mb: 2 }}
          />

          <Box sx={{ mt: 'auto', display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              onClick={onApplyFix}
              sx={{
                flex: 1,
                backgroundColor: '#EF8997',
                '&:hover': { backgroundColor: '#e57383' },
              }}
            >
              Apply Fix
            </Button>
            <Button
              variant="outlined"
              startIcon={<SkipNextIcon />}
              onClick={onSkipFix}
              sx={{ color: 'rgba(255,255,255,0.5)', borderColor: 'rgba(255,255,255,0.15)' }}
            >
              Skip
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

export default JointTestOverlay;
